import delegateListener from '../modules/delegate-listener';
import MediaItem from '../api/mediaitem';

import Cropper from 'cropperjs';
import Fabric from 'fabric';

class ImageEditor {
  constructor(options) {
    if(options.editMediaModal) {
      this.editMediaModal = options.editMediaModal;
    }
    this.cropButtonSelector = '.button[data-crop-media]';
    this.cropCommitSelector = '.button[data-crop-commit]';
    this.cropCancelSelector = '.button[data-crop-cancel]';
    this.shapeButtonSelector = '.button[data-shape-media]';
    this.shapeCommitSelector = '.button[data-shape-commit]';
    this.shapeCancelSelector = '.button[data-shape-cancel]';
    this.undoButtonSelector = '.button[data-undo-media]';
    this.uploadFormSelector = '.item-images__upload-form';
    this.canvasContainerSelector = '.image-editor-area';
    this.loaderContainerSelector = '.image-editor-loader';
    this.previousButtonSelector = '.button[data-modal-prev-image]';
    this.nextButtonSelector = '.button[data-modal-next-image]';

    this.black = '#000';
    this.red = '#fc6060';
    this.transparent = 'rgba(0,0,0,0)';

    // Cropper UI elements
    this.helperLineColor = '#080808';
    this.helperDotColor = '#f0f0f0';
    this.helperDotSizeBase = 7;
    this.lineWidthBase = 2;

    // Only used if image doesn't provide filename in it's [data-filename]
    this.defaultFilename = 'editor-image';

    this.jpegQuality = 0.98;

    // Cropper aspect ratio (forced)
    this.defaultAspect = 4/3;

    this.cropperOptions = {
      viewMode: 2,
      dragMode: 'crop',
      aspectRatio: this.defaultAspect,
      initialAspectRatio: this.defaultAspect,
      zoomable: false,
      toggleDragModeOnDblclick: false,
    };
  }

  init() {
    // exit early, if we're on a page that doesn't need this
    if(!document.body.classList.contains('page--edit-item-images')) return;

    document.body.addEventListener('click',
      delegateListener(
        this.cropButtonSelector + ':not(:disabled)',
        e => this.handleBeginCrop(e.delegateTarget.dataset.cropMedia)
      )
    );

    document.body.addEventListener('click',
      delegateListener(
        this.cropCommitSelector + ':not(:disabled)',
        e => this.handleCommitCrop(e.delegateTarget.dataset.cropCommit)
      )
    );

    document.body.addEventListener('click',
      delegateListener(
        this.cropCancelSelector + ':not(:disabled)',
        e => this.handleCancelCrop(e.delegateTarget.dataset.cropCancel)
      )
    );

    document.body.addEventListener('click',
      delegateListener(
        this.shapeButtonSelector + ':not(:disabled)',
        e => this.handleBeginShape(e)
      )
    );

    document.body.addEventListener('click',
      delegateListener(
        this.shapeCommitSelector + ':not(:disabled)',
        e => this.handleCommitShape(e.delegateTarget.dataset.shapeCommit)
      )
    );

    document.body.addEventListener('click',
      delegateListener(
        this.shapeCancelSelector + ':not(:disabled)',
        e => this.handleCancelShape(e.delegateTarget.dataset.shapeCancel)
      )
    );

    document.body.addEventListener('click',
      delegateListener(
        this.undoButtonSelector + ':not(:disabled)',
        e => this.handleUndoClick(e.delegateTarget.dataset.undoMedia)
      )
    );

    document.body.addEventListener('click',
      delegateListener(
        this.previousButtonSelector,
        e => this.editMediaModal._navigateMedia('prev')
      )
    );

    document.body.addEventListener('click',
      delegateListener(
        this.nextButtonSelector,
        e => this.editMediaModal._navigateMedia('next')
      )
    );
  }

  /**
   * Activates cropping mode
   *
   * @param {String|Number} mediaId - The current media ID
   */
  handleBeginCrop(mediaId) {
    const editorImage = document.querySelector(`img[data-editor-image="${ mediaId }"]`);
    this.fileName = editorImage.dataset.filename || this.defaultFilename;
    this.C = new Cropper(editorImage, this.cropperOptions);
    this.setCropActionsVisibility(true, mediaId);
  }

  /**
   * Commits current crop by initiating a save
   *
   * @param {String|Number} mediaId - The current media ID
   */
  handleCommitCrop(mediaId) {
    const canvas = this.C.getCroppedCanvas(this.cropOptions);
    this.submitImage(canvas, mediaId);
  }

  /**
   * Cancels current crop operation, discarding uncommitted changes
   * @param {String|Number} mediaId - The current media ID
   */
  handleCancelCrop(mediaId) {
    this.C.destroy();
    this.setCropActionsVisibility(false, mediaId)
  }

  /**
   * Hide/show or disable/enable UI components that shouldn't be touched while crop mode is active.
   *
   * @param {Boolean|*} visible - Should be true when crop mode is activated, false otherwise
   * @param {Number|String} mediaId - The current media ID
   */
  setCropActionsVisibility(visible, mediaId) {
    // Display crop commit/cancel buttons
    const cropTools = document.querySelector(`[data-crop-media="${ mediaId }"] + [data-crop-tools]`);
    cropTools.style.display = visible ? 'block' : '';

    // Disable buttons
    const cropButton = document.querySelector(`[data-crop-media="${ mediaId }"]`);
    const shapeButtons = Array.from(document.querySelectorAll(this.shapeButtonSelector));
    const modalCloseButtons = Array.from(document.querySelectorAll('[data-modal-close]'));
    const buttons = [ cropButton, ...shapeButtons, ...modalCloseButtons ];
    buttons.forEach(button => button.disabled = !!visible);
  }

  /**
   * Hide/show or disable/enable UI components that shouldn't be touched while shape mode is active.
   *
   * @param {Boolean|*} visible - Should be true when shape mode is activated, false otherwise
   * @param {Number|String} mediaId - The current media ID
   */
  setShapeActionsVisibility(visible, mediaId) {
    const shapeTools = document.querySelector(`[data-shape-media="${ mediaId }"] + [data-shape-tools]`);
    shapeTools.style.display = visible ? 'block' : '';

    // Disable buttons
    const cropButton = document.querySelector(`[data-crop-media="${ mediaId }"]`);
    const shapeButtons = Array.from(document.querySelectorAll(this.shapeButtonSelector));
    const modalCloseButtons = Array.from(document.querySelectorAll('[data-modal-close]'));
    const buttons = [ cropButton, ...shapeButtons, ...modalCloseButtons ];
    buttons.forEach(button => button.disabled = !!visible);
  }

  /**
   * Disables or undisables commit and cancel buttons
   * @param {Boolean} disabled - If truthy, buttons will be disabled. If falsey, buttons will be enabled
   */
  setCommitCancelDisabled(disabled = true) {
    const buttons = document.querySelectorAll([
      this.cropCommitSelector,
      this.cropCancelSelector,
      this.shapeCommitSelector,
      this.shapeCancelSelector
    ].join(' '));
    buttons.forEach(button => button.disabled = !!disabled);
  }

  /**
  * Activates shapes mode with the selected shape
  *
  * @param {Event} event - The initiating event object
  */
  handleBeginShape(event) {
    // get the selected shape URL
    const { svgUrl, shapeMedia, style } = event.delegateTarget.dataset;
    if(!svgUrl) return; // Button is missing data-svg-url attribute

    const mediaId = event.delegateTarget.dataset.shapeMedia;
    this.setShapeActionsVisibility(true, mediaId);

    // Reset fabric instance, in case there's a leftover
    if(this._fabricCanvas) {
      this.removeFabric();
    }

    // create a canvas that matches the image
    if(shapeMedia) {
      this.createCanvas(shapeMedia);
    }

    // Choose the right styles by dataset.style
    const isOutlined = style === 'outline';
    const isBlack = style.includes('black');

    // bring the selected shape into the canvas with Fabric
    // position it over the image..
    Fabric.fabric.loadSVGFromURL(svgUrl, (result) => {
      if(result.length > 0) {
        const theShape = result[0];
        if(isOutlined) {
          theShape.setColor(this.transparent).set({
            stroke: this.red,
            strokeWidth: this.lineWidth
          });
        } else if(isBlack) {
          theShape.setColor(this.black);
        } else {
          theShape.setColor(this.red);
        }
        this.addShapeToCanvas(result[0]);
      }
    });
  }

  /**
   * Inserts a fabric.js canvas on top of the current media image
   *
   * @param {String|Number} mediaId - The mediaID of the current image
   */
  createCanvas(mediaId) {
    const image = document.querySelector(`img[data-editor-image="${ mediaId }"]`);
    const container = document.querySelector(this.canvasContainerSelector);
    const width = image.naturalWidth || image.width;
    const height = image.naturalHeight || image.height;

    this._fabricCanvas = new Fabric.fabric.Canvas('shape-canvas-' + mediaId, {
      width, height,
      // This is disabled, because the uploaded image size
      // would otherwise double on each successive save!
      enableRetinaScaling: false
    });

    const fabricImage = new Fabric.fabric.Image(image);

    // Scale canvas with the image
    this._fabricCanvas.setDimensions({
      width: '100%', height: '100%'
    }, { cssOnly: true });

    // Scale helper dots and shape lines relative to image size
    const imageUnit = this.getImageUnit(image);
    this.helperDotSize = imageUnit * this.helperDotSizeBase;
    this.lineWidth = imageUnit * this.lineWidthBase;

    this.fileName = image.dataset.filename || this.defaultFilename;

    this._fabricCanvas.setBackgroundImage(fabricImage, () => {
      container.appendChild(this._fabricCanvas.wrapperEl);
      container.classList.remove('hidden'); // Show the shape editor container
    }, {
      left: 0, top: 0, width: width, height: height
    });
  }

  /**
   * Returns a unit relative to the image's apparent size
   *
   * @param {HTMLImageElement} image - The image the unit should be based on
   * @returns {Number} - The relative unit
   */
  getImageUnit(image) {
    return image.naturalWidth / image.offsetWidth;
  }

  /**
   * Commits current shape by initiating a save
   *
   * @param {String|Number} mediaId - The current media ID
   */
  handleCommitShape(mediaId) {
    // Deselect shape, otherwise it's guides will be visible in the rendered image.
    this._fabricCanvas.discardActiveObject();
    this._fabricCanvas.renderAll();

    this.submitImage(this._fabricCanvas.getElement(), mediaId);
  }


  /**
   * Cancels current crop operation, discarding uncommitted changes
   *
   * @param {String|Number} mediaId - The current media ID
   */
  handleCancelShape(mediaId) {
    this.removeFabric();
    this.setShapeActionsVisibility(false, mediaId);
  }

  /**
   * Removes current fabric instance from memory and DOM
   */
  removeFabric() {
    if(this._fabricCanvas) {
      const canvas = this._fabricCanvas.getElement();
      this._fabricCanvas.dispose();
      this._fabricCanvas = false;
      canvas.parentNode.removeChild(canvas);

      // Hide container to avoid blocking cropper tool
      document.querySelector(this.canvasContainerSelector).classList.add('hidden');
    }
  }

  /**
   * Adds an SVG shape to the fabric instance for editing
   * @param {Object} shape - The loaded SVG shape
   */
  addShapeToCanvas(shape) {
    if(!this._fabricCanvas) return;

    // Set shape style
    shape.set({
      borderColor: this.helperLineColor,
      cornerColor: this.helperDotColor,
      cornerSize: this.helperDotSize,
      transparentCorners: false
    });

    // Push shape into canvas
    this._fabricCanvas.add(shape);

    // Preselect the shape
    this._fabricCanvas.setActiveObject(shape);

    // Position shape at middle of canvas
    this._fabricCanvas.viewportCenterObject(shape);
  }

  /**
   * Merges the shapes with the image, and returns a single canvas element,
   * that can be used as the updated image.
   * @param {HTMLImageElement} image - The image element to use
   * @param {HTMLCanvasElement} canvas - The canvas element to use
   * @returns {HTMLCanvasElement} - The merged canvas element
   */
  mergeShapes(image, canvas) {
    const finalCanvas = canvas.toCanvasElement();
    const context = finalCanvas.getContext('2d');
    context.globalCompositeOperation = 'destination-over';
    context.drawImage(image, 0, 0, finalCanvas.width, finalCanvas.height);
    return finalCanvas;
  }

  /**
   * Sends the given canvas element's image data to the backend,
   * for replacing the current mediaitem's image
   * @param {HTMLCanvasElement} canvas - The canvas to send
   * @param {Number|String} mediaId - The MediaItem pid to update
   */
  submitImage(canvas, mediaId) {
    // Clear error messages, if any
    this.editMediaModal._triggerClearInfoEvent();
    // Display loading screen
    document.querySelector(this.loaderContainerSelector).classList.remove('hidden');
    // Disable buttons during AJAX
    this.setCommitCancelDisabled(true);

    const form = document.querySelector(this.uploadFormSelector);
    const submitURL = form.action;
    const formData = new FormData(form);

    // Remove image from image input field, just in case
    formData.delete('image');

    // Get canvas image data as JPEG
    canvas.toBlob(blob => {
      formData.append(
        'image', // name of the field
        blob, // image data
        this.trimExtension(this.fileName) + '.jpg' // image filename
      );
      formData.append('overwrites', mediaId);

      fetch(submitURL, {
        method: 'POST',
        body: formData
      })
        .then(r => r.json())
        .then(json => {
          if(json.status !== 'OK') {
            if(Array.isArray(json.messages) && json.messages.length > 0) {
              this.handleSubmitError(json.messages.join());
              return;
            }
            this.handleSubmitError(JSON.stringify(json, null, 2));
            return;
          }
          this.updateImageView(json, mediaId);
        })
        .catch(error => this.handleSubmitError(error.toString()));

    }, 'image/jpeg', this.jpegQuality);
  }

  /**
   * Display a helpful error message
   * @param {String} error - The error message
   */
  handleSubmitError(error) {
    this.editMediaModal._showInfo(error, 'error');
  }

  /**
   * Removes .jpg, .jpeg, .png or .gif extension from filename.
   * Also removes the indexed prefix, created by Studio
   * i.e. 'photo-8.jpg' => 'photo'
   * @param {String} filename - The filename to remove extension from
   * @returns {String} - The filename without extension
   */
  trimExtension(filename) {
    return filename.replace(/(-[0-9]+)?\.(jpg|jpeg|gif|png)$/i, '');
  }

  /**
   * Reloads image editor view, if the response indicates a successful upload
   * @param {Object} responseJson - The response JSON
   * @param {String|Number} mediaId - The pid of the MediaItem that was updated
   */
  updateImageView(responseJson, mediaId) {
    if(!responseJson.newMediaPid) {
      this.handleSubmitError('Something went wrong, please try again.');
      document.querySelector(this.loaderContainerSelector).classList.add('hidden');
      this.setCommitCancelDisabled(false);
    }
    this.editMediaModal.removeMediaModals();
    this._fabricCanvas = false;

    this.updateThumbnails(mediaId, responseJson.newMediaPid, responseJson.newMediaSrc);

    this.editMediaModal.createModal(responseJson.newMediaPid);
  }


  /**
   * Triggers the restore-previous API route, then reloads the view if successful.
   * @param {Number} mediaId - The pid of the MediaItem to restore
   */

  handleUndoClick(mediaId) {
    const mediaItem = new MediaItem(mediaId);
    mediaItem.restorePrevious().then(res => {
      if(res.status === 'OK') {
        this.editMediaModal.removeMediaModals();

        this.updateThumbnails(mediaId, res.newMediaPid, res.newMediaSrc);

        if(typeof res.newMediaPid === 'number') {
          this.editMediaModal.createModal(res.newMediaPid);
        }
      } else {
        if(Array.isArray(res.messages)) {
          this.handleSubmitError(res.messages.join());
        } else {
          this.handleSubmitError('Error: ' + JSON.stringify(res));
        }
      }
    });
  }


  /**
   * Updates the selected thumbnail's data-attributes and linked image locations
   *
   * @param {Number} oldPid - The pid of the thumbnail to update
   * @param {Number} newPid - The new MediaItem pid
   * @param {String} newImgUrl - The URL of the new image
   */
  updateThumbnails(oldPid, newPid, newImgUrl) {
    const thumbnail = document.querySelector(`[data-item-id="${ oldPid }"]`);
    const thumbButton = thumbnail.querySelector('[data-modal-target]');
    thumbnail.dataset.itemId = newPid;
    thumbButton.dataset.modalTarget = `edit-media-${ newPid }`;

    const thumbImage = thumbnail.querySelector('img.image-grid__image');
    const downloadLink = thumbnail.querySelector('a');
    thumbImage.src = newImgUrl;
    downloadLink.href = newImgUrl;
  }
}

export default ImageEditor;
